import axios, { AxiosInstance, AxiosError } from 'axios'
import { API_BASE_URL } from '@/config'

// Helper to clear auth state - we use a function reference to avoid circular deps
let clearAuthState: (() => void) | null = null
export const setAuthClearHandler = (handler: () => void) => {
  clearAuthState = handler
}

// Deduplicate concurrent token refresh requests
let refreshPromise: Promise<string> | null = null

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // In a read-only "view-as customer" tab, a per-tab token in sessionStorage
    // takes precedence so the admin's localStorage session is never used.
    const token = sessionStorage.getItem('view_as_token') || localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any

    // In a read-only view-as tab, never run token refresh or clear logic — the
    // per-tab token can't be refreshed and we must not touch the admin session.
    if (sessionStorage.getItem('view_as_token')) {
      return Promise.reject(error)
    }

    // Handle 401 Unauthorized or 403 Forbidden (Not authenticated) - token expired/missing
    const isAuthError = error.response?.status === 401 ||
      (error.response?.status === 403 && (error.response?.data as any)?.detail === 'Not authenticated')

    if (isAuthError && !originalRequest._retry) {
      originalRequest._retry = true

      const refreshToken = localStorage.getItem('refresh_token')
      if (refreshToken) {
        try {
          // Deduplicate: if a refresh is already in progress, wait for it
          if (!refreshPromise) {
            refreshPromise = axios.post(`${API_BASE_URL}/auth/refresh`, {
              refresh_token: refreshToken,
            }).then((response) => {
              const { access_token } = response.data
              localStorage.setItem('access_token', access_token)
              return access_token
            }).finally(() => {
              refreshPromise = null
            })
          }

          const access_token = await refreshPromise
          originalRequest.headers.Authorization = `Bearer ${access_token}`
          return api(originalRequest)
        } catch (refreshError) {
          // Only END the session if the refresh token itself was rejected
          // (401/403). A transient failure — network blip, timeout, 5xx, or a
          // backend restart — must NOT log an active user out: their refresh
          // token is still valid and will succeed on the next attempt. Previously
          // ANY failure here destroyed the session, so a momentary hiccup at the
          // 30-min token-refresh point booted active users ("timing out too fast").
          const status = (refreshError as AxiosError)?.response?.status
          if (status === 401 || status === 403) {
            localStorage.removeItem('access_token')
            localStorage.removeItem('refresh_token')
            localStorage.removeItem('user')
            localStorage.removeItem('auth-storage')
            if (clearAuthState) clearAuthState()
          }
          return Promise.reject(refreshError)
        }
      } else {
        // No refresh token, clear auth state and update Zustand
        localStorage.removeItem('access_token')
        localStorage.removeItem('user')
        localStorage.removeItem('auth-storage')
        if (clearAuthState) clearAuthState()
      }
    }

    return Promise.reject(error)
  }
)

export default api

// Helper function to extract error message
export const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ detail?: string | { msg: string }[] }>
    if (axiosError.response?.data?.detail) {
      const detail = axiosError.response.data.detail
      if (typeof detail === 'string') {
        return detail
      } else if (Array.isArray(detail)) {
        return detail.map((d) => d.msg).join(', ')
      }
    }
    return axiosError.message
  }
  if (error instanceof Error) {
    return error.message
  }
  return 'An unexpected error occurred'
}
